import { getLocale } from 'next-intl/server'

import { HTTP_TIMEOUT_DEFAULT, requirePublicApiBaseUrl } from '@/configs/api'

const API_PREFIX = '/api'

export const apiPaths = {
  customers: '/customers',
  directoryItems: '/directory-items',
  directoryGroups: '/directory-groups',
  homeTrustLogos: '/home/trust-logos',
  blogs: '/blogs',
  categories: '/categories',
  categoryBySlug: (slug: string) => `/categories/${encodeURIComponent(slug)}`,
  blogBySlug: (slug: string) => `/blogs/${encodeURIComponent(slug)}`,
  careers: '/careers',
  careerBySlug: (slug: string) => `/careers/detail/${encodeURIComponent(slug)}`,
  departments: '/departments',
} as const

export function buildPublicApiUrl(path: string, params?: Record<string, string>): string {
  const base = requirePublicApiBaseUrl().replace(/\/+$/, '')
  const url = new URL(`${base}${API_PREFIX}${path}`)

  if (params) {
    Object.entries(params).forEach(([k, v]) => {
      url.searchParams.set(k, v)
    })
  }

  return url.toString()
}

export async function fetchPublicApiJson<T>(
  url: string,
  options?: {
    revalidateSeconds?: number
    tags?: string[]
    timeoutMs?: number
    skipLangParam?: boolean
  },
): Promise<T> {
  const locale = options?.skipLangParam ? null : await getLocale().catch(() => null)
  if (locale) {
    const parsed = new URL(url)
    if (!parsed.searchParams.has('lang')) parsed.searchParams.set('lang', locale)
    url = parsed.toString()
  }

  const controller = new AbortController()
  const timeoutId = setTimeout(
    () => controller.abort(),
    options?.timeoutMs ?? HTTP_TIMEOUT_DEFAULT,
  )

  try {
    const res = await fetch(url, {
      cache: 'force-cache',
      next: {
        revalidate: options?.revalidateSeconds ?? 300,
        tags: options?.tags,
      },
      headers: { Accept: 'application/json' },
      signal: controller.signal,
    })

    if (!res.ok) {
      const text = await res.text().catch(() => '')
      throw new Error(
        `API error ${res.status} ${res.statusText}${text ? ` — ${text}` : ''} [${url}]`,
      )
    }

    return (await res.json()) as T
  } finally {
    clearTimeout(timeoutId)
  }
}
